import React, {useContext, useState} from 'react';
import Box from "@mui/material/Box";
import {Button, Grid, Typography} from "@mui/material";
import { LayoutContext } from "../../contexts/LayoutContext/LayoutContext";
import {OgMetaTagsPages} from "./OgMetaTags";
import {CountdownToEvent} from "../CountdownToEvent/CountdownToEvent";
import ReceiptIcon from '@mui/icons-material/Receipt';
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PlaylistPlayIcon from '@mui/icons-material/PlaylistPlay';
import {Link} from "react-router-dom";
import {PagesList, youTubeEmbedPlaylistCITS} from "../../config";

// Store aspect ratio in one place
const ASPECT_RATIO = '1/1.414'; // A4 ratio

const YouTubePlayer = ({ playlist, isVisible }) => {
    const { isMobile, isTablet } = useContext(LayoutContext);

    if (!isVisible) return null;

    return (
        <div style={{
            width: isMobile ? "100%" : isTablet ? "80%" : "70%",
            aspectRatio: '16/9',
            position: "relative"
        }}>
            <iframe
                style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                }}
                src={playlist.replace('&amp;', '&')}
                title="YouTube video player"
                allow="accelerometer; encrypted-media; gyroscope"
                referrerPolicy="no-referrer-when-downgrade"
                loading="lazy"
                allowFullScreen
            />
        </div>
    );
};

// Description component with read more toggle
const DescriptionWithReadMore = ({ text, fontColor, variant, participatingArtists }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const lines = text.split('\n');
    const firstTwoLines = lines.slice(0, 1).join('\n');
    const hasMoreLines = lines.length > 1;

    return (
        <Typography
            textAlign="center"
            variant={variant}
            style={{
                whiteSpace: 'pre-line',
                color: fontColor,
                marginTop: 10,
                marginBottom: 10
            }}
        >
            {isExpanded ? text : firstTwoLines}
            {hasMoreLines && (
                <>
                    {!isExpanded && '... '}
                    <Button
                        onClick={() => setIsExpanded(!isExpanded)}
                        sx={{
                            color: fontColor,
                            textTransform: 'none',
                            minWidth: 'auto',
                            padding: 0,
                            fontWeight: 'bold',
                            '&:hover': {
                                backgroundColor: 'transparent',
                                textDecoration: 'underline',
                            }
                        }}
                    >
                        {isExpanded ? 'Read less' : 'Read more'}
                    </Button>
                </>
            )}
            <br/>
            <br/>
            {participatingArtists.map((artist, index) => (
                <React.Fragment key={index}>
                    {index === 0 ? (
                        <>
                            <strong>
                                <a href={artist.link} target="_blank" rel="noopener noreferrer">
                                    {artist.name}
                                </a>
                            </strong>
                            <br/>
                        </>
                    ) : (
                        <>
                            <strong>
                                <a href={artist.link} target="_blank" rel="noopener noreferrer">
                                    {artist.name}
                                </a>
                            </strong>
                            <br/>
                        </>
                    )}
                </React.Fragment>
            ))}
            <br/>
            <span style={{ color: '#005cbe', fontWeight: 'bolder', backgroundColor: '#fff' }}>Limited Capacity</span>
        </Typography>
    );
};

export const EventPage = ({ EventDetails }) => {

    const { isMobile } = useContext(LayoutContext);
    const [showPlaylist, setShowPlaylist] = useState(false);
    const buttonsAndTimeBackgroundColor = EventDetails.buttonsAndTimeBackgroundColor
    const buttonsAndTimeFontColor = EventDetails.buttonsAndTimeFontColor
    const countDownLabelsColor = EventDetails.countDownLabelsColor
    const onHoverColor = EventDetails.onHoverColor
    const windowsBackground = EventDetails.windowsBackground
    const fontColor = EventDetails.fontColor

    const mobileButtonSx = {
        flex: 1,
        minWidth: 0,
        height: 60,
        lineHeight: 1,
        px: "3px",
        py: 0,

        backgroundColor: buttonsAndTimeBackgroundColor,
        color: buttonsAndTimeFontColor,

        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "clip",
        fontSize: "clamp(9px, 2.3vw, 13px)",

        "& .MuiButton-startIcon": {
            marginRight: "2px",
            marginLeft: 0,
        },

        "& .MuiSvgIcon-root": {
            fontSize: "16px",
        },

        "&:hover": {
            color: "#fff",
            backgroundColor: onHoverColor,
        },

        "&:focus": {
            color: "#fff",
            backgroundColor: onHoverColor,
        },
    };

    // Get the styles for the Grid
    const getGridStyles = () => {
        const baseStyles = {
            paddingTop: isMobile ? 0 : 30,
            paddingBottom: isMobile ? 0 : 30,
        };

        if (!isMobile) {
            return {
                ...baseStyles,
                backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url(${EventDetails.eventCover})`,
                backgroundSize: 'contain',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                backgroundColor: '#000',
            };
        }

        return baseStyles;
    };

    // Toggle playlist visibility
    const togglePlaylist = () => {
        setShowPlaylist(!showPlaylist);
    };

    return(
        <Grid
            container
            direction="column"
            justify="center"
            alignItems="center"
            style={getGridStyles()}
        >
            <OgMetaTagsPages content={EventDetails} />

            {/* Mobile: Image as component covering full width */}
            {isMobile && (
                <Box
                    component="img"
                    src={EventDetails.eventCover}
                    alt="Event Cover"
                    sx={{
                        width: '100%',
                        aspectRatio: ASPECT_RATIO,
                        objectFit: 'cover',
                        objectPosition: 'center',
                    }}
                />
            )}

            <Box
                sx={{
                    width: isMobile ? '100%' : '55%',
                    aspectRatio: isMobile ? 'auto' : ASPECT_RATIO,
                    justifyContent: 'center',
                    display: 'flex',
                    flexDirection: 'column'
                }}
            >
                {/* Desktop View */}
                <Box
                    display={ isMobile ? 'none' : 'flex' }
                    flexDirection="column"
                    width="100%"
                    gap={2}
                    alignItems="center"
                    sx={{
                        backgroundColor: `${windowsBackground}99`, // 99 = 60% opacity
                    }}
                    paddingTop={2}
                    paddingBottom={1}
                >
                    <Box width="99%" display="flex" justifyContent="center" flexDirection="row" fontSize={10} gap={1}>
                        <Button
                            sx={{
                                backgroundColor: buttonsAndTimeBackgroundColor,
                                color: buttonsAndTimeFontColor,
                                height: 60,
                                width: EventDetails.ticketsLink && EventDetails.facebookEventPage ? '25%' : '33%',
                                '&:hover': {
                                    color: '#fff',
                                    backgroundColor: onHoverColor,
                                },
                                '&:focus': {
                                    color: '#fff',
                                    backgroundColor: onHoverColor,
                                },
                            }}
                            onClick={() => window.open(EventDetails.goToVenueFromOwnLocation, '_blank')}
                            variant="outlined"
                        >
                            <LocationOnIcon/>
                            Get there
                        </Button>
                        {EventDetails.ticketsLink  && (
                            <Button
                                sx={{
                                    backgroundColor: buttonsAndTimeBackgroundColor,
                                    color: buttonsAndTimeFontColor,
                                    height: 60,
                                    width: EventDetails.facebookEventPage ? '25%' : '33%',
                                    animation: 'pulseBackground 5s infinite ease-in-out',
                                    '@keyframes pulseBackground': {
                                        '0%': {
                                            backgroundColor: buttonsAndTimeBackgroundColor,
                                        },
                                        '50%': {
                                            backgroundColor: onHoverColor,
                                        },
                                        '100%': {
                                            backgroundColor: buttonsAndTimeBackgroundColor,
                                        },
                                    },
                                    '&:hover': {
                                        color: '#fff',
                                        backgroundColor: onHoverColor,
                                    },
                                    '&:focus': {
                                        color: '#fff',
                                        backgroundColor: onHoverColor,
                                    },
                                }}
                                onClick={() => window.open(EventDetails.ticketsLink, '_blank')}
                                variant="outlined"
                            >
                                <ReceiptIcon sx={{ mr: 1 }} />
                                Tickets
                            </Button>
                        )}
                        <Button
                            sx={{
                                backgroundColor: buttonsAndTimeBackgroundColor,
                                color: buttonsAndTimeFontColor,
                                height: 60,
                                width: EventDetails.ticketsLink && EventDetails.facebookEventPage ? '25%' : '33%',
                                '&:hover': {
                                    color: '#fff',
                                    backgroundColor: onHoverColor,
                                },
                                '&:focus': {
                                    color: '#fff',
                                    backgroundColor: onHoverColor,
                                },
                            }}
                            onClick={togglePlaylist}
                            variant="outlined"
                        >
                            <PlaylistPlayIcon sx={{ mr: 1 }} />
                            {showPlaylist ? 'Hide' : 'Playlist'}
                        </Button>
                        {EventDetails.facebookEventPage && (
                            <Button
                                sx={{
                                    backgroundColor: buttonsAndTimeBackgroundColor,
                                    color: '#ffffff',
                                    height: 60,
                                    width: EventDetails.ticketsLink ? '25%' : '33%',
                                    '&:hover': {
                                        color: '#395693',
                                        backgroundColor: onHoverColor,
                                    },
                                    '&:focus': {
                                        color: '#fff',
                                        backgroundColor: onHoverColor,
                                    },
                                }}
                                onClick={() => window.open(EventDetails.facebookEventPage, '_blank')}
                                variant="outlined"
                            >
                                Facebook Event
                            </Button>
                        )}
                    </Box>
                    <YouTubePlayer
                        playlist={EventDetails.youTubePlaylist ? EventDetails.youTubePlaylist : youTubeEmbedPlaylistCITS}
                        isVisible={showPlaylist}
                    />
                    <CountdownToEvent
                        completeDate={EventDetails.eventCountDown}
                        backgroundColor={buttonsAndTimeBackgroundColor}
                        fontColor={buttonsAndTimeFontColor}
                        labelsColor={countDownLabelsColor}
                    />
                    <DescriptionWithReadMore
                        text={EventDetails.description}
                        fontColor={fontColor}
                        variant="h5"
                        participatingArtists={EventDetails.participatingArtists}
                    />
                </Box>
            </Box>

            {/* Mobile View */}
            <Box display={ isMobile ? 'flex' : 'none' } flexDirection="column" alignItems="center" gap={1} bgcolor={`${windowsBackground}99`} paddingTop={1} paddingBottom={1}>
                <Box
                    width="99%"
                    display="flex"
                    flexDirection="row"
                    gap={0.2}
                    alignItems="stretch"
                >
                    <Button
                        sx={mobileButtonSx}
                        startIcon={<LocationOnIcon />}
                        onClick={() => window.open(EventDetails.goToVenueFromOwnLocation, "_blank")}
                        variant="outlined"
                    >
                        Get there
                    </Button>
                    {EventDetails.ticketsLink  && (
                        <Button
                            sx={{
                                ...mobileButtonSx,
                                animation: "pulseBackground 5s infinite ease-in-out",
                                "@keyframes pulseBackground": {
                                    "0%": {
                                        backgroundColor: buttonsAndTimeBackgroundColor,
                                    },
                                    "50%": {
                                        backgroundColor: onHoverColor,
                                    },
                                    "100%": {
                                        backgroundColor: buttonsAndTimeBackgroundColor,
                                    },
                                },
                            }}
                            startIcon={<ReceiptIcon />}
                            onClick={() => window.open(EventDetails.ticketsLink, "_blank")}
                            variant="outlined"
                        >
                            Tickets
                        </Button>
                    )}
                    <Button
                        sx={mobileButtonSx}
                        startIcon={<PlaylistPlayIcon />}
                        onClick={togglePlaylist}
                        variant="outlined"
                    >
                        {showPlaylist ? "Hide" : "Playlist"}
                    </Button>
                    {EventDetails.facebookEventPage && (
                        <Button
                            sx={{
                                ...mobileButtonSx,
                                color: "#fff",
                                "&:hover": {
                                    ...mobileButtonSx["&:hover"],
                                    color: "#395693",
                                },
                            }}
                            onClick={() => window.open(EventDetails.facebookEventPage, "_blank")}
                            variant="outlined"
                        >
                            Facebook Event
                        </Button>
                    )}
                </Box>
                <YouTubePlayer
                    playlist={EventDetails.youTubePlaylist ? EventDetails.youTubePlaylist : youTubeEmbedPlaylistCITS}
                    isVisible={showPlaylist}
                />
                <Box
                    sx={{
                        width: '100%',
                        transform: isMobile ? 'scale(0.7)' : 'scale(1)',
                        transformOrigin: 'center center',
                        transition: 'transform 0.3s ease',
                        '& > *': {
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                        }
                    }}
                >
                    <CountdownToEvent
                        completeDate={EventDetails.eventCountDown}
                        backgroundColor={buttonsAndTimeBackgroundColor}
                        fontColor={buttonsAndTimeFontColor}
                        labelsColor={countDownLabelsColor}
                    />
                </Box>
                <DescriptionWithReadMore
                    text={EventDetails.description}
                    fontColor={fontColor}
                    variant="h6"
                    participatingArtists={EventDetails.participatingArtists}
                />
            </Box>
        </Grid>
    )
}